import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { setLanguage } from "@/lib/i18n";

const AuthContext = createContext(null);

const IMP_TOKEN_KEY = "akm_imp_token";
const IMP_ORIG_TOKEN_KEY = "akm_imp_orig_token";
const IMP_ORIG_USER_KEY = "akm_imp_orig_user";

export function AuthProvider({ children }) {
    const [user, setUser] = useState(undefined); // undefined=loading, null=guest, object=user
    const [loading, setLoading] = useState(true);
    const [impersonation, setImpersonation] = useState(null); // { by: {id,email,name} }

    const applyUserLanguage = (u) => {
        if (u?.language) setLanguage(u.language);
    };

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            applyUserLanguage(data);
            if (data.impersonated_by) {
                setImpersonation({ by: data.impersonated_by });
            } else {
                setImpersonation(null);
            }
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    const login = async (email, password, remember = false) => {
        try {
            const { data } = await api.post("/auth/login", { email, password, remember });
            if (data.access_token) localStorage.setItem("akm_token", data.access_token);
            setUser(data.user);
            applyUserLanguage(data.user);
            setImpersonation(null);
            return { ok: true, user: data.user };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            /* noop */
        }
        localStorage.removeItem("akm_token");
        localStorage.removeItem(IMP_TOKEN_KEY);
        localStorage.removeItem(IMP_ORIG_TOKEN_KEY);
        localStorage.removeItem(IMP_ORIG_USER_KEY);
        setUser(null);
        setImpersonation(null);
    };

    // Start impersonation: save original token & swap to target token
    const startImpersonation = useCallback(async (targetUserId) => {
        try {
            const origToken = localStorage.getItem("akm_token");
            const { data } = await api.post(`/superadmin/users/${targetUserId}/impersonate`);
            if (!data.access_token) throw new Error("No token returned");
            // stash original identity
            if (origToken) localStorage.setItem(IMP_ORIG_TOKEN_KEY, origToken);
            localStorage.setItem(IMP_ORIG_USER_KEY, JSON.stringify(user));
            localStorage.setItem("akm_token", data.access_token);
            localStorage.setItem(IMP_TOKEN_KEY, data.access_token);
            setUser(data.user);
            setImpersonation({ by: data.impersonated_by });
            return { ok: true, user: data.user };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    }, [user]);

    // Stop impersonation: hit backend to revoke the impersonation session and
    // receive a fresh super_admin token. Falls back to the client-side token
    // swap if the backend endpoint isn't available.
    const stopImpersonation = useCallback(async () => {
        try {
            const { data } = await api.post("/auth/impersonate/exit");
            if (data?.access_token) {
                localStorage.setItem("akm_token", data.access_token);
                localStorage.removeItem(IMP_TOKEN_KEY);
                localStorage.removeItem(IMP_ORIG_TOKEN_KEY);
                localStorage.removeItem(IMP_ORIG_USER_KEY);
                await fetchMe();
                return { ok: true };
            }
        } catch (e) {
            // Backend may return 400 "Not an impersonated session" if the current
            // token has no `impersonated_by` — in that case fall through to the
            // legacy client-side restore (older clients still work).
        }
        const origToken = localStorage.getItem(IMP_ORIG_TOKEN_KEY);
        if (!origToken) return { ok: false };
        localStorage.setItem("akm_token", origToken);
        localStorage.removeItem(IMP_TOKEN_KEY);
        localStorage.removeItem(IMP_ORIG_TOKEN_KEY);
        localStorage.removeItem(IMP_ORIG_USER_KEY);
        await fetchMe();
        return { ok: true };
    }, [fetchMe]);

    return (
        <AuthContext.Provider value={{
            user, loading, login, logout, refresh: fetchMe,
            impersonation, startImpersonation, stopImpersonation,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
